/**
 * Pickleball traditional side-out scoring engine (Casual Arena).
 * USA Pickleball Rulebook — only serving side scores.
 *
 * Doubles:
 * - Opening team starts at server #2 (0-0-2)
 * - Even score → serve right; odd → left (own team score)
 * - On score: place continuing server in score-matching court; partner opposite
 * - #1 fault → #2 from current court (no move); #2 fault → side-out
 * - Receiving team does not rotate until they serve and score
 *
 * Singles: even/odd court; one fault = side-out.
 *
 * L/R courts ARE persisted rules state (USAPA) — exception to ADR-033 TT derive model.
 *
 * scoringSystem: "side_out"
 */

import {
  wrapSerializedMatch,
  unwrapSerializedMatch,
} from "../../contracts/MatchEngine";
import { playersFromTeam, setsToWinToBestOf } from "../../../matchScoringBridge";
import { cloneMatchSnapshot } from "../../shared/cloneMatchSnapshot";
import {
  advanceToSecondServerTransition,
  applySideOutTransition,
  awardServePointTransition,
  courtOfPlayer,
  otherSide,
  playerInCourt,
  serveCourtForScore,
  servingFaultAction,
} from "./pbRules";

export const PICKLEBALL_ENGINE_ID = "pickleball.side_out.v1";
export const PICKLEBALL_SCORING_SYSTEM = "side_out";
const SCHEMA_VERSION = 2;

export const PICKLEBALL_FAULT_TYPES = Object.freeze([
  { id: "bounce_ok", label: "Bounce OK (2-bounce)" },
  { id: "early_volley", label: "Early Volley (2-bounce)" },
  { id: "ball_out", label: "Ball Out" },
  { id: "net", label: "Net" },
  { id: "kitchen", label: "Kitchen" },
  { id: "double_bounce", label: "Double Bounce" },
  { id: "wrong_serve", label: "Wrong Serve" },
  { id: "player_hit", label: "Player Hit" },
  { id: "other", label: "Other" },
]);

function emptyTwoBounce() {
  return { serveBounce: false, returnBounce: false, volleyAllowed: false };
}

/** Advance USA Pickleball two-bounce phases (receive bounce → return bounce → volleys OK). */
function advanceTwoBounce(game) {
  const tb = game.twoBounce || emptyTwoBounce();
  if (!tb.serveBounce) {
    return {
      ...game,
      twoBounce: { serveBounce: true, returnBounce: false, volleyAllowed: false },
    };
  }
  if (!tb.returnBounce) {
    return {
      ...game,
      twoBounce: { serveBounce: true, returnBounce: true, volleyAllowed: true },
    };
  }
  return { ...game, twoBounce: { ...tb, volleyAllowed: true } };
}

function twoBounceHint(twoBounce) {
  if (!twoBounce || twoBounce.volleyAllowed) return null;
  if (!twoBounce.serveBounce) return "Two-bounce: await receive bounce";
  if (!twoBounce.returnBounce) return "Two-bounce: await return bounce";
  return "Two-bounce in effect";
}

function clonePlayers(list) {
  return (list || []).map((p) => ({ ...p }));
}

function buildSides(teamA, teamB) {
  return {
    A: { players: clonePlayers(playersFromTeam(teamA)), score: 0 },
    B: { players: clonePlayers(playersFromTeam(teamB)), score: 0 },
  };
}

/** Designate starting courts: first slot = right/even, second = left/odd. */
function withStartingCourts(side, mode) {
  const players = side.players || [];
  const rightPlayerId = players[0]?.id || null;
  const leftPlayerId =
    mode === "doubles" && players.length > 1 ? players[1]?.id || null : null;
  return {
    ...side,
    rightPlayerId,
    leftPlayerId,
    /** Frozen for the game — validates even-court player / UI. */
    initialRightPlayerId: rightPlayerId,
  };
}

function getSideState(game, side) {
  return side === "A" ? game.sideA : game.sideB;
}

function createGame(mode, sides, servingSide, serverNumber, pointsToWin, winBy) {
  const sideA = withStartingCourts({ ...sides.A, score: 0 }, mode);
  const sideB = withStartingCourts({ ...sides.B, score: 0 }, mode);
  const serving = servingSide === "A" ? sideA : sideB;
  // Start-at-2: first server (right/even), labeled #2 so one fault → side-out.
  const serverId = serving.rightPlayerId || serving.players[0]?.id || null;
  return withServeActors({
    mode,
    sideA,
    sideB,
    servingSide,
    serverNumber: mode === "doubles" ? serverNumber : 1,
    serverId,
    serveCourt: serveCourtForScore(0),
    rallyCount: 0,
    twoBounce: emptyTwoBounce(),
    pointsToWin,
    winBy,
    gameWinner: null,
    history: [],
  });
}

function sideScore(game, side) {
  return side === "A" ? game.sideA.score : game.sideB.score;
}

function pushHistory(game, entry) {
  return { ...game, history: [...(game.history || []), entry] };
}

/** Diagonal receiver: same relative court on the opposite side. */
function resolveReceiverId(game) {
  const recv = getSideState(game, otherSide(game.servingSide));
  if (game.mode !== "doubles") {
    return recv.players[0]?.id || recv.rightPlayerId || null;
  }
  return playerInCourt(recv, game.serveCourt || "right") || null;
}

function withServeActors(game) {
  const receiverId = resolveReceiverId(game);
  return { ...game, receiverId };
}

function refreshServeFromPositions(game) {
  const serving = getSideState(game, game.servingSide);
  const score = sideScore(game, game.servingSide);
  if (game.mode === "singles") {
    return withServeActors({
      ...game,
      serverId: serving.players[0]?.id || serving.rightPlayerId || null,
      serveCourt: serveCourtForScore(score),
      twoBounce: emptyTwoBounce(),
    });
  }
  const serverId = game.serverId || serving.rightPlayerId || null;
  return withServeActors({
    ...game,
    serverId,
    serveCourt: courtOfPlayer(serving, serverId),
    twoBounce: emptyTwoBounce(),
  });
}

function applySideOut(game) {
  const t = applySideOutTransition(game, game.mode);
  return withServeActors({
    ...game,
    ...t,
    twoBounce: emptyTwoBounce(),
  });
}

function advanceToSecondServer(game) {
  const t = advanceToSecondServerTransition(game);
  return withServeActors({
    ...game,
    ...t,
    twoBounce: emptyTwoBounce(),
  });
}

function bumpRally(game) {
  return { ...game, rallyCount: (game.rallyCount || 0) + 1 };
}

function awardPointToServer(game) {
  const side = game.servingSide;
  const t = awardServePointTransition(game);
  let next = bumpRally({
    ...game,
    sideA: t.sideA,
    sideB: t.sideB,
  });
  next = withServeActors({
    ...next,
    serveCourt: t.serveCourt,
    twoBounce: emptyTwoBounce(),
  });
  next = pushHistory(next, { type: "point", side });
  if (t.gameWinner) {
    return { ...next, gameWinner: t.gameWinner };
  }
  return next;
}

/**
 * Serving team fault → next server or side-out.
 * Receiving team fault → point to server.
 */
function applyFaultOnGame(game, faultType, faultingSide) {
  const side = faultingSide || otherSide(game.servingSide);
  let next = pushHistory(game, { type: "fault", faultType, side });
  if (side !== next.servingSide) {
    return awardPointToServer(next);
  }
  next = bumpRally(next);
  const action = servingFaultAction(next);
  if (action === "second_server") {
    return advanceToSecondServer(next);
  }
  return applySideOut(next);
}

function gamesWon(match, side) {
  return (match.games || []).filter((g) => g.gameWinner === side).length;
}

function matchWinnerFromGames(match) {
  const need = Math.ceil((match.bestOf || 1) / 2);
  if (gamesWon(match, "A") >= need) return "A";
  if (gamesWon(match, "B") >= need) return "B";
  return null;
}

/**
 * Court UI expects slots[0]=right court, slots[1]=left court.
 * Reorder from engine rightPlayerId/leftPlayerId after serving-team score swaps.
 */
function reorderTeamForCourts(team, sideState) {
  if (!team || !sideState) return team;
  const rightId = sideState.rightPlayerId;
  const leftId = sideState.leftPlayerId;
  if (!rightId || !leftId || !team.slots || team.slots.length < 2) return team;

  const byId = new Map();
  for (const slot of team.slots) {
    if (slot.registryEntryId) byId.set(slot.registryEntryId, slot);
    if (slot.playerId) byId.set(slot.playerId, slot);
  }
  const right = byId.get(rightId);
  const left = byId.get(leftId);
  if (!right || !left) return team;
  if (team.slots[0] === right && team.slots[1] === left) return team;
  return { ...team, slots: [right, left] };
}

function syncPickleballTeamsToBoxes(teamA, teamB, match) {
  const g = match?.activeGame;
  if (!g || g.mode !== "doubles") return { teamA, teamB };
  return {
    teamA: reorderTeamForCourts(teamA, g.sideA),
    teamB: reorderTeamForCourts(teamB, g.sideB),
  };
}

/** Official call: serving score first, then receiving (+ server# in doubles). */
function formatServeDisplay(game) {
  if (!game) return null;
  const serving = sideScore(game, game.servingSide);
  const receiving = sideScore(game, otherSide(game.servingSide));
  if (game.mode === "doubles") {
    return `${serving}-${receiving}-${game.serverNumber}`;
  }
  return `${serving}-${receiving}`;
}

/**
 * @returns {import("../../contracts/MatchEngine").MatchEngine}
 */
export function createPickleballMatchEngine() {
  const engine = {
    engineId: PICKLEBALL_ENGINE_ID,
    scoringSystem: PICKLEBALL_SCORING_SYSTEM,

    startMatch(config) {
      const mode = config.teamA?.type === "doubles" ? "doubles" : "singles";
      const sides = buildSides(config.teamA, config.teamB);
      const pointsToWin = Math.max(1, config.pointsToWin ?? 11);
      const winBy = 2;
      const servingSide = config.firstServerSide || "A";
      // Official start: serving team begins as server #2 (0-0-2)
      const serverNumber = mode === "doubles" ? 2 : 1;
      const game = createGame(
        mode,
        sides,
        servingSide,
        serverNumber,
        pointsToWin,
        winBy
      );
      return {
        engineId: PICKLEBALL_ENGINE_ID,
        scoringSystem: PICKLEBALL_SCORING_SYSTEM,
        bestOf: setsToWinToBestOf(config.setsToWin ?? 2),
        endsSwapped: !!config.endsSwapped,
        games: [],
        activeGame: refreshServeFromPositions(game),
        matchWinner: null,
        intervalBreak: null,
        awaitingNextGame: false,
        undoStack: [],
      };
    },

    startNextGame(match, config) {
      if (!match?.games?.length) {
        throw new Error("startNextGame: no completed games");
      }
      const mode = config.teamA?.type === "doubles" ? "doubles" : "singles";
      const sides = buildSides(config.teamA, config.teamB);
      const last = match.games[match.games.length - 1];
      const pointsToWin = Math.max(1, config.pointsToWin ?? last.pointsToWin ?? 11);
      const servingSide = config.firstServerSide || "A";
      const serverNumber = mode === "doubles" ? 2 : 1;
      const game = createGame(
        mode,
        sides,
        servingSide,
        serverNumber,
        pointsToWin,
        last.winBy || 2
      );
      const { awaitingNextGame: _drop, ...rest } = match;
      return {
        ...rest,
        endsSwapped: !!config.endsSwapped,
        activeGame: refreshServeFromPositions(game),
        intervalBreak: null,
        awaitingNextGame: false,
        matchWinner: null,
        undoStack: [],
      };
    },

    scorePoint(match, side) {
      if (!match?.activeGame || match.matchWinner) return match;
      const game = match.activeGame;
      const snapshot = cloneMatchSnapshot(match);
      let nextGame;
      if (side === game.servingSide) {
        nextGame = awardPointToServer(game);
      } else {
        // Receiver wins rally → side-out / next server (traditional side-out)
        nextGame = applyFaultOnGame(game, "rally_lost", game.servingSide);
      }
      return finalizeGameResult(match, nextGame, snapshot);
    },

    fault(match, faultType, side) {
      if (!match?.activeGame || match.matchWinner) return match;
      const snapshot = cloneMatchSnapshot(match);
      const type = faultType || "other";
      if (type === "bounce_ok") {
        return {
          ...match,
          activeGame: advanceTwoBounce(match.activeGame),
          undoStack: [...(match.undoStack || []), snapshot].slice(-40),
        };
      }
      const nextGame = applyFaultOnGame(
        match.activeGame,
        type,
        side || null
      );
      return finalizeGameResult(match, nextGame, snapshot);
    },

    undo(match) {
      if (!match?.undoStack?.length) return match;
      const prev = match.undoStack[match.undoStack.length - 1];
      return prev;
    },

    canUndo(match) {
      return Boolean(match?.undoStack?.length);
    },

    getState(match) {
      return match || null;
    },

    getLiveView(match) {
      if (!match) {
        return {
          scoreA: 0,
          scoreB: 0,
          serving: "A",
          sets: [],
          winner: null,
          endsSwapped: false,
          serveDisplay: null,
          extras: null,
        };
      }
      const sets = (match.games || []).map((g) => ({
        scoreA: g.sideA.score,
        scoreB: g.sideB.score,
        winner: g.gameWinner,
      }));
      const g = match.activeGame;
      const serve = formatServeDisplay(g);
      const gameNum = (match.games || []).length + 1;
      const twoBounce = g?.twoBounce;
      const twoBounceHintText = twoBounceHint(twoBounce);
      return {
        scoreA: g?.sideA.score ?? 0,
        scoreB: g?.sideB.score ?? 0,
        serving: g?.servingSide ?? "A",
        sets,
        winner: match.matchWinner,
        endsSwapped: !!match.endsSwapped,
        serveDisplay: serve,
        extras: g
          ? {
              serverNumber: g.serverNumber,
              serverId: g.serverId,
              receiverId: g.receiverId,
              serveCourt: g.serveCourt,
              rallyCount: g.rallyCount || 0,
              twoBounce: g.twoBounce,
              mode: g.mode,
              courts: {
                A: {
                  rightPlayerId: g.sideA.rightPlayerId,
                  leftPlayerId: g.sideA.leftPlayerId,
                  initialRightPlayerId: g.sideA.initialRightPlayerId,
                },
                B: {
                  rightPlayerId: g.sideB.rightPlayerId,
                  leftPlayerId: g.sideB.leftPlayerId,
                  initialRightPlayerId: g.sideB.initialRightPlayerId,
                },
              },
              phaseLabel: `GAME ${gameNum}`,
              scoreMeta: [
                serve ? `Serve ${serve}` : null,
                g.serveCourt ? `${String(g.serveCourt).toUpperCase()} court` : null,
                twoBounceHintText,
              ]
                .filter(Boolean)
                .join(" · "),
            }
          : null,
      };
    },

    getWinner(match) {
      return match?.matchWinner ?? null;
    },

    serialize(match) {
      if (!match) return null;
      return wrapSerializedMatch(PICKLEBALL_ENGINE_ID, SCHEMA_VERSION, match);
    },

    deserialize(json) {
      const unwrapped = unwrapSerializedMatch(json);
      if (!unwrapped.state) return null;
      if (unwrapped.legacy) return null;
      if (unwrapped.engineId && unwrapped.engineId !== PICKLEBALL_ENGINE_ID) {
        return null;
      }
      return unwrapped.state;
    },

    isGamePoint(match) {
      const g = match?.activeGame;
      if (!g) return false;
      const server = sideScore(g, g.servingSide);
      const recv = sideScore(g, otherSide(g.servingSide));
      return server + 1 >= g.pointsToWin && server + 1 - recv >= g.winBy;
    },

    isMatchPoint(match) {
      if (!this.isGamePoint(match)) return false;
      const need = Math.ceil((match.bestOf || 1) / 2);
      const serving = match.activeGame.servingSide;
      return gamesWon(match, serving) + 1 >= need;
    },

    hasInterval() {
      return false;
    },

    resumeInterval(match) {
      return match;
    },

    isAwaitingCourtSetup(match) {
      return Boolean(match?.awaitingNextGame);
    },

    suggestCourtSetup(match) {
      const last = match?.games?.[match.games.length - 1];
      return {
        suggestedServerSide: last?.gameWinner === "A" ? "B" : "A",
        endsSwapped: !match?.endsSwapped,
      };
    },

    syncTeamsToBoxes(teamA, teamB, match) {
      return syncPickleballTeamsToBoxes(teamA, teamB, match);
    },

    listFaultTypes() {
      return [...PICKLEBALL_FAULT_TYPES];
    },

    setsToWinToBestOf,
  };

  // Legacy bridge-shaped aliases used by useLiveScoring during migration
  return {
    ...engine,
    initScoringMatch(teamA, teamB, setsToWin, endsSwapped, firstServerSide, pointsToWin) {
      return engine.startMatch({
        teamA,
        teamB,
        setsToWin,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    startNextGameFromCourtSetup(existing, teamA, teamB, endsSwapped, firstServerSide, pointsToWin) {
      return engine.startNextGame(existing, {
        teamA,
        teamB,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    matchToLiveView: engine.getLiveView,
    serializeScoringMatch: engine.serialize,
    deserializeScoringMatch: engine.deserialize,
    recordRally: engine.scorePoint,
    undoLastRally: engine.undo,
    canUndoRally: engine.canUndo,
    resumeFromInterval: engine.resumeInterval,
    suggestCourtSetupAfterGame: engine.suggestCourtSetup,
    syncTeamsToActiveGameBoxes: engine.syncTeamsToBoxes,
  };
}

function finalizeGameResult(match, nextGame, snapshot) {
  if (!nextGame.gameWinner) {
    return {
      ...match,
      activeGame: nextGame,
      undoStack: [...(match.undoStack || []), snapshot].slice(-40),
    };
  }
  const games = [...(match.games || []), nextGame];
  const provisional = { ...match, games, activeGame: null };
  const winner = matchWinnerFromGames(provisional);
  if (winner) {
    return {
      ...provisional,
      matchWinner: winner,
      awaitingNextGame: false,
      undoStack: [...(match.undoStack || []), snapshot].slice(-40),
    };
  }
  return {
    ...provisional,
    matchWinner: null,
    awaitingNextGame: true,
    endsSwapped: !match.endsSwapped,
    undoStack: [...(match.undoStack || []), snapshot].slice(-40),
  };
}
